import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { XCircleIcon } from 'lucide-react';
export function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const failedApproaches = ['The "One More Bite" rule creates a power struggle where the child eventually wins by refusing everything.', 'Hiding vegetables creates "food anxiety"—once they find a green speck in a "safe" food, they stop trusting anything you serve.', 'Bribing with dessert teaches them that healthy food is a "chore" that must be suffered through to get a reward.', 'Short-order cooking reinforces the idea that they never have to interact with anything outside of their 5 "safe" foods.'];
  return <section ref={ref} className="w-full bg-white py-10 md:py-16 px-4" data-id="element-131">
      <div className="max-w-3xl mx-auto" data-id="element-132">
        {/* Opening Hook */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-10" data-id="element-133">
          <p className="font-['Source_Sans_3'] text-lg md:text-xl text-[#3A3A3A] italic leading-relaxed border-l-4 border-[#E8DDD0] pl-6 py-2" data-id="element-134">
            It's 6:00 PM. You just spent forty minutes in the kitchen preparing
            a healthy meal, and the moment you set the plate down, the air
            leaves the room. Your child takes one look, pushes the plate away,
            and screams, "Ew! Yucky! I want nuggets!"
          </p>
          <p className="font-['Source_Sans_3'] text-lg md:text-xl text-[#3A3A3A] italic leading-relaxed border-l-4 border-[#E8DDD0] pl-6 py-2 mt-4" data-id="element-135">
            You feel that familiar knot of defeat in your stomach. You try to
            bargain. You try the "one brave bite" rule. You try to explain why
            they need vitamins. Nothing works.
          </p>
          <p className="font-['Source_Sans_3'] text-lg md:text-xl text-[#3A3A3A] italic leading-relaxed border-l-4 border-[#E8DDD0] pl-6 py-2 mt-4" data-id="element-136">
            Ten minutes later, you're back at the stove making a grilled cheese
            just so they don't go to bed hungry.
          </p>
        </motion.div>

        {/* The Problem */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.2
      }} className="mb-10" data-id="element-137">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#2D4A3E] mb-5" data-id="element-138">
            Sound Familiar?
          </h2>
          <p className="font-['Source_Sans_3'] text-base md:text-lg text-[#3A3A3A] leading-relaxed mb-4" data-id="element-139">
            Most parenting advice tells you to "just keep offering it" or "hide
            the veggies." But you know the truth:
          </p>
          <p className="font-['Source_Sans_3'] text-base md:text-lg text-[#3A3A3A] leading-relaxed mb-4" data-id="element-140">
            <strong data-id="element-141">
              Hiding broccoli in a muffin destroys the one thing you need—trust.
            </strong>{' '}
            And "keep offering it" without a plan is just a repeated threat that
            makes your child's biological safety switch flip to "OFF."
          </p>
          <p className="font-['Source_Sans_3'] text-base md:text-lg text-[#3A3A3A] leading-relaxed" data-id="element-142">
            You've become a short-order cook in your own home. You're exhausted,
            your partner is frustrated, and family dinner has become a
            battlefield instead of a connection.
          </p>
        </motion.div>

        {/* Why What You Tried Didn't Work */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.4
      }} data-id="element-143">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#2D4A3E] mb-8" data-id="element-144">
            Why Nothing Has Worked (Yet)
          </h2>
          <div className="space-y-4" data-id="element-145">
            {failedApproaches.map((approach, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.5 + index * 0.1
          }} className="flex items-start gap-4 bg-[#FFF8F0] p-4 rounded-lg border border-[#E8DDD0]" data-id="element-146">
                <XCircleIcon className="w-6 h-6 text-[#C4665A] flex-shrink-0 mt-0.5" data-id="element-147" />
                <p className="font-['Source_Sans_3'] text-base text-[#3A3A3A] leading-relaxed" data-id="element-148">
                  {approach}
                </p>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}